import { useState, useEffect } from 'react';
import { Modal } from '../modal';
import { Button } from '../button';
import { TextField } from '../text-field';
import { Select } from '../select';
import { useSelected } from '../../providers/selectedContext';
import { updateUserRoleFromProject } from '../../api/data';
import { User, UserRole } from '../../@types/users/user';
import { Project } from '../../@types/projects/types';

export interface EditMemberModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (project: Project) => void;
  user: User | null;
}

const ROLE_OPTIONS = [
  { value: UserRole.READER, label: 'Reader' },
  { value: UserRole.WRITER, label: 'Writer' },
];

const formStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
};

const footerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '8px',
};

const EditMemberModal = ({ open, onClose, onSuccess, user }: EditMemberModalProps) => {
  const { selectedInfo } = useSelected();
  const [role,    setRole]    = useState<string>(user?.role ?? UserRole.READER);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && user) {
      setRole(user.role);
      setLoading(false);
    }
  }, [open, user?.email]);

  const handleSave = async () => {
    const project = selectedInfo.project;
    if (!project || !user) return;
    setLoading(true);
    try {
      const response = await updateUserRoleFromProject(project.uid, user.email, role as UserRole);
      onSuccess(response);
      onClose();
    } catch (error) {
      console.error('ERROR WHILE UPDATING MEMBER ROLE:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit Role"
      size="sm"
      footer={
        <div style={footerStyle}>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} loading={loading}>
            Update Role
          </Button>
        </div>
      }
    >
      <div style={formStyle}>
        <TextField
          label="Email"
          fullWidth
          value={user?.email ?? ''}
          disabled
        />
        <Select
          label="Role"
          fullWidth
          options={ROLE_OPTIONS}
          value={role}
          onChange={val => setRole(val)}
        />
      </div>
    </Modal>
  );
};

export { EditMemberModal };
